import { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Search,
  RefreshCcw,
  Loader2,
  ShieldAlert
} from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import ImpactProduitsModal from './ImpactProduitsModal';
import PremiumButton from './PremiumButton';

const IngredientsManager = ({ commercantId, isSuperAdmin = false }) => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [impactModal, setImpactModal] = useState({
    open: false,
    ingredient: null,
    loading: false,
    data: null,
    error: ''
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(null);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [showRuptureModal, setShowRuptureModal] = useState(false);
  const [ingredientForRupture, setIngredientForRupture] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    stock: null,
    allergenes: [],
    prix: 0,
    unite: 'g'
  });
  const [allergenesInput, setAllergenesInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingAvailability, setTogglingAvailability] = useState(false);

  useEffect(() => {
    fetchIngredients();
  }, [commercantId]);

  const fetchIngredients = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Récupérer TOUTES les pages (comme ProduitsManager)
      let allIngredients = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 100; // Maximum autorisé par le backend
      
      while (hasMore) {
        const endpoint = commercantId && !isSuperAdmin
          ? `/ingredients?commercantId=${commercantId}&page=${page}&pageSize=${pageSize}`
          : `/ingredients?page=${page}&pageSize=${pageSize}`;
        
        const response = await apiService.request(endpoint);
        
        // ⭐ CORRECTION : Vérifier data?.data (structure paginée)
        const ingredientsPage = Array.isArray(response?.data) 
          ? response.data 
          : Array.isArray(response) 
          ? response 
          : [];
        
        allIngredients = [...allIngredients, ...ingredientsPage];
        
        // Vérifier s'il y a encore des ingrédients à récupérer
        const total = response?.pagination?.total || response?.total || 0;
        hasMore = allIngredients.length < total && ingredientsPage.length === pageSize;
        page++;
      }
      
      setIngredients(allIngredients);
    } catch (err) {
      console.error('Ingredients error:', err);
      setError('Erreur lors du chargement des ingrédients');
      setIngredients([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredIngredients = ingredients.filter((ingredient) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    return (
      (ingredient.nom || '').toLowerCase().includes(query) ||
      (ingredient.description || '').toLowerCase().includes(query)
    );
  });

  const handleEdit = (ingredient) => {
    setSelectedIngredient(ingredient);
    setFormData({
      nom: ingredient.nom || '',
      description: ingredient.description || '',
      stock: ingredient.stock ?? null,
      allergenes: Array.isArray(ingredient.allergenes) ? ingredient.allergenes : [],
      prix: ingredient.prix ?? 0,
      unite: ingredient.unite || 'g'
    });
    setAllergenesInput(
      Array.isArray(ingredient.allergenes) && ingredient.allergenes.length > 0
        ? ingredient.allergenes.join(', ')
        : ''
    );
    setShowModal(true);
  };

  const handleNew = () => {
    setSelectedIngredient(null);
    setFormData({
      nom: '',
      description: '',
      stock: null,
      allergenes: [],
      prix: 0,
      unite: 'g'
    });
    setAllergenesInput('');
    setShowModal(true);
  };

  const handleViewImpact = async (ingredient) => {
    if (!commercantId) {
      setImpactModal({
        open: true,
        ingredient,
        loading: false,
        data: null,
        error: 'Commerçant requis pour afficher l\'impact.',
      });
      return;
    }
    setImpactModal({
      open: true,
      ingredient,
      loading: true,
      data: null,
      error: ''
    });
    try {
      const data = await apiService.request(
        `/ingredients/${ingredient.id}/impact?commercantId=${encodeURIComponent(commercantId)}`
      );
      setImpactModal((prev) => ({
        ...prev,
        loading: false,
        data
      }));
    } catch (impactError) {
      console.error('Erreur impact ingrédients:', impactError);
      setImpactModal((prev) => ({
        ...prev,
        loading: false,
        error: "Impossible de récupérer l'impact sur les produits."
      }));
    }
  };

  const closeImpactModal = () => {
    setImpactModal({
      open: false,
      ingredient: null,
      loading: false,
      data: null,
      error: ''
    });
  };

  const handleMettreEnRupture = (ingredient) => {
    setIngredientForRupture(ingredient);
    setShowRuptureModal(true);
  };

  const handleReactiver = async (ingredient) => {
    if (!ingredient || !commercantId) return;
    
    if (!confirm(`Réactiver l'ingrédient "${ingredient.nom}" ?`)) {
      return;
    }

    try {
      setSaving(true);
      
      // Si on a déjà un stockId, l'utiliser directement
      if (ingredient.stockId) {
        await apiService.request(`/stock-ingredients/${ingredient.stockId}`, {
          method: 'PUT',
          body: JSON.stringify({
            disponible: true
          })
        });
      } else {
        // Sinon, chercher le stock ou le créer
        const stockIngredients = await apiService.request(`/stock-ingredients?commercantId=${commercantId}`);
        const existingStock = stockIngredients.find(s => s.ingredientId === ingredient.id);

        if (existingStock) {
          await apiService.request(`/stock-ingredients/${existingStock.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              disponible: true
            })
          });
        } else {
          // Créer un nouveau stock disponible
          await apiService.request('/stock-ingredients', {
            method: 'POST',
            body: JSON.stringify({
              commercantId,
              ingredientId: ingredient.id,
              stockDisponible: 0,
              stockMinimum: 0,
              unite: 'kg',
              disponible: true
            })
          });
        }
      }

      await fetchIngredients();
    } catch (err) {
      console.error('Erreur lors de la réactivation:', err);
      alert('Erreur lors de la réactivation');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRupture = async (selection) => {
    if (!ingredientForRupture || !commercantId) return;

    const produitsToDisable = {
      produits: selection.produits || [],
      elements: selection.elements || []
    };

    try {
      setSaving(true);
      
      if (ingredientForRupture.stockId) {
        await apiService.request(`/stock-ingredients/${ingredientForRupture.stockId}`, {
          method: 'PUT',
          body: JSON.stringify({
            disponible: false,
            produitsToDisable
          })
        });
      } else {
        const stockIngredients = await apiService.request(`/stock-ingredients?commercantId=${commercantId}`);
        const existingStock = stockIngredients.find(s => s.ingredientId === ingredientForRupture.id);

        if (existingStock) {
          await apiService.request(`/stock-ingredients/${existingStock.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              disponible: false,
              produitsToDisable
            })
          });
        } else {
          await apiService.request('/stock-ingredients', {
            method: 'POST',
            body: JSON.stringify({
              commercantId,
              ingredientId: ingredientForRupture.id,
              stockDisponible: 0,
              stockMinimum: 0,
              unite: 'kg',
              disponible: false,
              produitsToDisable
            })
          });
        }
      }

      await fetchIngredients();
      setShowRuptureModal(false);
      setIngredientForRupture(null);
    } catch (err) {
      console.error('Erreur lors de la mise en rupture:', err);
      alert('Erreur lors de la mise en rupture');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (ingredient) => {
    setShowConfirmDialog({
      id: ingredient.id,
      nom: ingredient.nom
    });
  };

  const confirmDeleteIngredient = async () => {
    if (!showConfirmDialog?.id) {
      setShowConfirmDialog(null);
      return;
    }
    try {
      await apiService.request(`/ingredients/${showConfirmDialog.id}`, {
        method: 'DELETE'
      });
      setShowConfirmDialog(null);
      await fetchIngredients();
    } catch (deleteError) {
      console.error('Erreur suppression:', deleteError);
      alert(
        "Impossible de supprimer cet ingrédient. Vérifiez qu'il n'est pas utilisé par des produits actifs."
      );
      setShowConfirmDialog(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) {
      alert('Le nom est obligatoire');
      return;
    }

    const allergenesArray = allergenesInput
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    const payload = {
      nom: formData.nom.trim(),
      description: formData.description?.trim() || null,
      stock: formData.stock === null || formData.stock === '' ? null : Number(formData.stock),
      prix: Number.isFinite(parseFloat(formData.prix)) ? parseFloat(formData.prix) : 0,
      allergenes: allergenesArray,
      unite: formData.unite || 'g',
      // actif n'est plus géré ici - utilisez "Mettre en rupture" pour gérer la disponibilité
      commercantId: commercantId || null // Lier l'ingrédient au commerçant si fourni
    };

    try {
      setSaving(true);
      if (selectedIngredient) {
        await apiService.request(`/ingredients/${selectedIngredient.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        await apiService.request('/ingredients', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      await fetchIngredients();
      setShowModal(false);
      setSelectedIngredient(null);
    } catch (saveError) {
      console.error('Erreur lors de la sauvegarde:', saveError);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };


  const totalActifs = ingredients.filter((item) => item.actif !== false).length;
  const totalInactifs = ingredients.length - totalActifs;

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ingrédients & ruptures</h1>
            <p className="text-sm text-gray-600">
              Signalez une matière première indisponible : les produits liés deviennent non commandables.
            </p>
          </div>
          {!isSuperAdmin && (
            <button onClick={handleNew} className="btn-primary inline-flex items-center gap-2">
              <Plus className="h-5 w-5" />
              <span>Nouvel ingrédient</span>
            </button>
          )}
          {isSuperAdmin && (
            <div className="text-sm text-gray-500 italic">
              Vue en lecture seule - Les ingrédients sont créés par les commerçants
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <ShieldAlert className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ingrédients actifs</p>
                <p className="text-2xl font-bold text-gray-900">{totalActifs}</p>
              </div>
              <ToggleRight className="h-10 w-10 text-green-500 opacity-20" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ingrédients inactifs</p>
                <p className="text-2xl font-bold text-gray-900">{totalInactifs}</p>
              </div>
              <ToggleLeft className="h-10 w-10 text-gray-400 opacity-20" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un ingrédient par nom ou description..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchIngredients}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                disabled={loading}
              >
                <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Rafraîchir
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Chargement des ingrédients...
            </div>
          ) : filteredIngredients.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-gray-500">
              <Trash2 className="h-12 w-12 text-gray-300" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Aucun ingrédient</h3>
                <p className="text-sm text-gray-500">
                  {searchTerm
                    ? "Aucun ingrédient ne correspond à votre recherche."
                    : "Ajoutez vos matières premières ou laissez l'assistant les créer automatiquement depuis les produits."}
                </p>
              </div>
              {!isSuperAdmin && (
                <PremiumButton
                  commercantId={commercantId}
                  permission="GESTION_STOCK"
                  featureName="Gestion des Ingrédients"
                  description="Créez et gérez vos ingrédients avec suivi de stock avancé"
                  benefits={[
                    'Suivi en temps réel de vos stocks',
                    'Alertes automatiques de rupture',
                    'Impact sur les produits associés',
                    'Gestion des allergènes'
                  ]}
                  onClick={handleNew}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Ajouter un ingrédient
                </PremiumButton>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Ingrédient</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Statut</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Produits</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredIngredients.map((ingredient) => {
                    const isActive = ingredient.actif !== false;
                    // ⭐ Statut de stock (rupture/disponible) pour ce commerçant
                    const enRupture = ingredient.enRupture === true;
                    const stockStatus = ingredient.stockStatus; // 'RUPTURE' | 'DISPONIBLE' | null
                    
                    return (
                      <tr key={ingredient.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <span className="font-medium text-gray-900">{ingredient.nom}</span>
                            {ingredient.description && (
                              <p className="text-xs text-gray-500 line-clamp-2">
                                {ingredient.description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {!isSuperAdmin && (
                              <>
                                {enRupture ? (
                                  <button
                                    onClick={() => handleReactiver(ingredient)}
                                    disabled={saving}
                                    className="inline-flex items-center px-3 py-1.5 border border-green-300 text-sm font-medium rounded-lg text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Réactiver
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleMettreEnRupture(ingredient)}
                                    disabled={saving}
                                    className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Mettre en rupture
                                  </button>
                                )}
                              </>
                            )}
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                enRupture
                                  ? 'bg-red-100 text-red-700'
                                  : isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {enRupture ? 'Rupture' : isActive ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-gray-600">
                            {ingredient._count?.produits ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleViewImpact(ingredient)}
                              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 transition"
                            >
                              Impact
                            </button>
                            <PremiumButton
                              commercantId={commercantId}
                              permission="GESTION_STOCK"
                              featureName="Modifier un ingrédient"
                              description="Modifiez les propriétés de cet ingrédient"
                              benefits={['Modifier le nom, description, prix', 'Gérer les allergènes', 'Ajuster le stock']}
                              onClick={() => !isSuperAdmin && handleEdit(ingredient)}
                              disabled={isSuperAdmin}
                              className={`rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition ${isSuperAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              Modifier
                            </PremiumButton>
                            <PremiumButton
                              commercantId={commercantId}
                              permission="GESTION_STOCK"
                              featureName="Supprimer un ingrédient"
                              description="Supprimez cet ingrédient de votre catalogue"
                              benefits={['Nettoyer votre catalogue', 'Supprimer les données obsolètes']}
                              onClick={() => !isSuperAdmin && handleDelete(ingredient)}
                              disabled={isSuperAdmin}
                              className={`rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 transition ${isSuperAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              Supprimer
                            </PremiumButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedIngredient ? `Modifier ${selectedIngredient.nom}` : 'Ajouter un ingrédient'}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Ex : Saumon frais"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Notes sur l'ingrédient, origine, qualité..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix unitaire</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={formData.prix}
                  onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
                <span className="text-sm text-gray-500">€/{formData.unite || 'g'}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unité</label>
              <select
                value={formData.unite}
                onChange={(e) => setFormData({ ...formData, unite: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="L">L</option>
                <option value="unité">unité</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock disponible</label>
              <input
                type="number"
                value={formData.stock ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    stock: e.target.value === '' ? null : Number(e.target.value)
                  })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Laissez vide pour illimité"
                min="0"
              />
              <p className="mt-1 text-xs text-gray-500">
                Laissez vide pour un stock illimité. Indiquez une valeur pour suivre une quantité.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergènes (séparés par des virgules)
              </label>
              <textarea
                value={allergenesInput}
                onChange={(e) => setAllergenesInput(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Ex : gluten, poisson, œufs"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>{selectedIngredient ? 'Enregistrer' : 'Ajouter'}</span>
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={impactModal.open}
        onClose={closeImpactModal}
        title={
          impactModal.ingredient
            ? `Impact de "${impactModal.ingredient.nom}"`
            : "Impact de l'ingrédient"
        }
        maxWidth="max-w-3xl"
      >
        <div className="space-y-4">
          {impactModal.loading && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Analyse des produits liés...
            </div>
          )}
          {!impactModal.loading && impactModal.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {impactModal.error}
            </div>
          )}
          {!impactModal.loading && impactModal.data && (
            <>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                <p>
                  <span className="font-semibold text-gray-900">
                    {impactModal.data.produitsAffectes?.length || 0}
                  </span>{' '}
                  produit(s) utilisent cet ingrédient. Ils seront désactivés si vous le rendez
                  indisponible.
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700">Produit</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700">Commerçant</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-700">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(impactModal.data.produitsAffectes || []).map((produit) => (
                      <tr key={produit.id}>
                        <td className="px-4 py-2 text-gray-900">{produit.nom}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {produit.commercant?.nom || '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              produit.actif
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {produit.actif ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(showConfirmDialog)}
        onCancel={() => setShowConfirmDialog(null)}
        onConfirm={confirmDeleteIngredient}
        title={
          showConfirmDialog
            ? `Supprimer ${showConfirmDialog.nom} ?`
            : 'Supprimer cet ingrédient ?'
        }
        description="Cette action est définitive. Assurez-vous qu'aucun produit n'utilise encore cet ingrédient."
        tone="danger"
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
      />

      <ImpactProduitsModal
        isOpen={showRuptureModal}
        onClose={() => {
          setShowRuptureModal(false);
          setIngredientForRupture(null);
        }}
        ingredient={ingredientForRupture}
        commercantId={commercantId}
        onConfirm={handleConfirmRupture}
      />
    </>
  );
};

export default IngredientsManager;

